import { useState, useRef, useEffect, ReactNode } from "react";
import { t } from "i18next";

import {
  getLicensePlates,
  getParkingSettings,
  getUser,
  selectTimeslot,
  useAppDispatch,
  useAppSelector,
} from "@lib/store";
import { trackEvent } from "@lib/infrastructure";
import { Icon, IconNames } from "@atoms";
import {
  constructOfficeWorkdayDraft,
  useExtraServiceUrl,
  usePreselectTimeslot,
  useWorkdayActionList,
  useWorkdayBasedExtraServices,
} from "@lib/logic";
import { Button } from "@molecules";
import { cn } from "@utils";
import { useModal } from "@hooks";
import { AllExtraServicesSelectionCard } from "src/components/organisms/extras";
import { CreateEventCard } from "src/components/organisms/createEvent";
import { BookingBuildingSelectionCard, BookWorkspaceCard } from "src/components/organisms/bookWorkspace";
import { ParkingSelectionCard } from "src/components/organisms/parking";
import { LicensePlatesModalPage } from "src/components/organisms/profile/licensePlates/LicensePlatesModalPage/LicensePlatesModalPage";
import { WorkdayDetailsModal } from "../WorkdayDetailsModal";
import { WorkdaySelectorCard } from "../WorkdaySelectorCard";
import { getFirstAvailableWorkspaceReservationTimeslot } from "@lib/store/src/data/timeslots/selectors";

interface Props {
  selectedDate: string;
}

const ICON_MAP: Record<string, IconNames> = {
  workspace: "desk",
  "meeting-room": "meetingRoom",
  parking: "car",
  services: "extras",
  "workday-status": "location",
};

export const WorkdayActions = ({ selectedDate }: Props) => {
  const dispatch = useAppDispatch();
  const { addPages, openModal, setModalPages } = useModal();
  const user = useAppSelector(getUser);

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { items, firstAvailableWorkdayTimeslot, officeWorkdays, isAnyVisible } = useWorkdayActionList(selectedDate);

  const firstOfficeWorkday = officeWorkdays?.[0];
  const firstAvailableWorkspaceReservationTimeslot = useAppSelector(
    getFirstAvailableWorkspaceReservationTimeslot(firstOfficeWorkday),
  );

  // Services
  const { highlightedServices, highlightedServicesCount } = useWorkdayBasedExtraServices(selectedDate);
  const url = useExtraServiceUrl(highlightedServices.at(0), undefined, selectedDate);

  // Parking
  const parkingSettings = useAppSelector(getParkingSettings);
  const licensePlates = useAppSelector(getLicensePlates);
  const isLicensePlateMandatory = parkingSettings?.licensePlateIsRequired ?? false;
  const isLicensePlateNeeded = isLicensePlateMandatory && !licensePlates?.length;

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const preselectTimeslot = usePreselectTimeslot({ dateKey: selectedDate });

  if (!isAnyVisible) return null;

  const handleItemClick = (key: string) => {
    setIsOpen(false);

    switch (key) {
      case "workspace":
        preselectTimeslot();

        let pages: ReactNode[];

        if (!firstOfficeWorkday) {
          // No planned workdays, use draft workday
          pages = [
            <BookingBuildingSelectionCard
              date={selectedDate}
              onSelect={building => {
                const draft = constructOfficeWorkdayDraft({
                  user,
                  selectedDate,
                  building,
                });

                addPages([<BookWorkspaceCard workday={draft} />]);
              }}
            />,
          ];
        } else if (officeWorkdays?.length > 1) {
          // Multiple planned workdays, let user choose one
          pages = [
            <WorkdaySelectorCard
              workdays={officeWorkdays}
              onSelect={workday => addPages([<BookWorkspaceCard workday={workday} />])}
            />,
          ];
        } else {
          // Preselect based workspace reservation timeslot availability
          dispatch(
            selectTimeslot(
              firstAvailableWorkspaceReservationTimeslot ?? {
                startTime: firstOfficeWorkday.localStart,
                endTime: firstOfficeWorkday.localEnd,
              },
            ),
          );

          // Exactly one planned workday, use it directly
          pages = [<BookWorkspaceCard workday={firstOfficeWorkday} />];
        }

        setModalPages(pages);
        openModal();
        break;
      case "meeting-room":
        addPages([<CreateEventCard date={selectedDate} />]);
        openModal();

        break;
      case "parking":
        if (!firstOfficeWorkday) return;

        trackEvent("StartParkingReservationInDayOverview");

        if (isLicensePlateNeeded) {
          setModalPages([
            <LicensePlatesModalPage
              date={selectedDate}
              buildingId={firstOfficeWorkday.buildingId}
            />,
          ]);
        } else {
          if (officeWorkdays?.length > 1) {
            setModalPages([
              <WorkdaySelectorCard
                workdays={officeWorkdays}
                onSelect={workday =>
                  addPages([
                    <ParkingSelectionCard
                      date={selectedDate}
                      buildingId={workday.buildingId}
                    />,
                  ])
                }
              />,
            ]);
          } else {
            setModalPages([
              <ParkingSelectionCard
                date={selectedDate}
                buildingId={firstOfficeWorkday.buildingId}
              />,
            ]);
          }
        }

        openModal();

        break;
      case "services":
        const isOnlyOneService = highlightedServicesCount === 1;
        const firstService = highlightedServices[0];

        if (isOnlyOneService) {
          trackEvent("ExtraServices_OpenService", {
            serviceType: firstService.serviceType,
            category: firstService.category,
            buildingId: firstService.buildingId,
          });

          window.open(url, "_blank");
        } else {
          trackEvent("CalendarOverview_ExtraServices__OpenMore");

          addPages([
            <AllExtraServicesSelectionCard
              category="WorkdayBased"
              whitelistedBuidlings={Array.from(new Set(highlightedServices.map(s => s.buildingId)))}
              locationName={undefined}
              meetingId={undefined}
              selectedDate={selectedDate}
            />,
          ]);

          openModal();
        }
        break;
      case "workday-status":
        setModalPages([<WorkdayDetailsModal selectedDate={selectedDate} />]);
        openModal();

        break;
    }
  };

  return (
    <div
      className="relative inline-block text-left ml-auto"
      ref={dropdownRef}>
      <Button
        onClick={() => setIsOpen(prev => !prev)}
        iconLeft="plus"
        className="whitespace-nowrap">
        {t("timeslots:AddBooking")}
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white border-gray-200 rounded-md shadow-lg z-50 overflow-hidden">
          {items
            .filter(item => item.visible)
            .map((item, index) => (
              <button
                key={item.title}
                disabled={!!item.disabledReason}
                onClick={() => handleItemClick(item.key)}
                className={cn(
                  "w-full h-[70px] px-5 flex items-center md:hover:bg-gray-50 disabled:hover:bg-white disabled:text-mapiq-black-500 disabled:cursor-not-allowed",
                  {
                    "border-t": index !== 0,
                  },
                )}>
                <Icon
                  icon={ICON_MAP[item.key]}
                  className={cn("mr-3 size-5 flex-shrink-0", {
                    "text-mapiq-black-500": !item.visible,
                  })}
                  color="currentColor"
                />

                <div>
                  <p>{item.title}</p>
                  {item.disabledReason && <p className="text-xs text-mapiq-black-500">{item.disabledReason}</p>}
                </div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
};
